import { CONCEPT_ICONS } from "@/components/ui/concept-icons";
import type { AgentCursor as AgentCursorData } from "@/stores/canvas-store";
import { getCurrentDenkerWs } from "@/api/canvas-ws-ref";
import { Button, Icons } from "@/components/ui";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { cursorsMessages } from "@/i18n/messages/cursors";
import { AgentStatusText } from "./agent-status-text";
import { AgentStreamingText } from "./agent-streaming-text";

interface AgentAvatarCardContentProps {
  cursor: AgentCursorData;
  statusText: string;
  testId: string;
  executionId: string | null;
  hasActions: boolean;
  canAskFollowUp: boolean;
  compact?: boolean;
  showAgentName?: boolean;
  onNavigateToFrame: () => void;
  onAskFollowUp: (mode: "voice" | "text") => void;
}

export function AgentAvatarCardContent({
  cursor,
  statusText,
  testId,
  executionId,
  hasActions,
  canAskFollowUp,
  compact = false,
  showAgentName = true,
  onNavigateToFrame,
  onAskFollowUp,
}: AgentAvatarCardContentProps) {
  const t = useT(cursorsMessages);
  const replyText = cursor.contentFull ?? cursor.content;
  const isReply = !cursor.error && !!replyText && statusText === replyText;
  return (
    <div className={cn(compact && "flex h-full min-h-0 flex-col")}>
      {showAgentName ? (
        <div
          className="mb-1 shrink-0 text-appkit-mini font-semibold"
          style={{ color: cursor.color }}
        >
          {cursor.name}
        </div>
      ) : null}
      <div
        className={cn(
          "whitespace-pre-wrap",
          compact
            ? "min-h-0 flex-1 overflow-y-auto break-words"
            : "max-h-48 overflow-hidden",
        )}
        data-testid={`${testId}-body`}
      >
        {isReply ? (
          <AgentStreamingText
            text={statusText}
            scope={JSON.stringify([
              cursor.agentId,
              cursor.executionId,
              cursor.sessionId,
            ])}
            live={!cursor.frozen}
          />
        ) : (
          <AgentStatusText
            text={statusText}
            working={!cursor.frozen && !cursor.error}
          />
        )}
      </div>
      {executionId ? (
        <div className="mt-3 flex shrink-0 justify-end">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={(event) => {
              event.stopPropagation();
              getCurrentDenkerWs()?.stopExecution(executionId);
            }}
            className="h-7 shrink-0 gap-1.5 rounded-control px-2 text-appkit-small"
            aria-label={t("stopAgent")}
            data-testid={`${testId}-stop-execution`}
          >
            <Icons.Square className="h-3 w-3 fill-current" />
            {t("stop")}
          </Button>
        </div>
      ) : null}
      {hasActions ? (
        <div
          className={cn(
            "mt-3 shrink-0 gap-2",
            compact
              ? "flex flex-col items-stretch"
              : "grid h-7 grid-cols-[minmax(0,1fr)_auto] items-center",
          )}
        >
          {canAskFollowUp ? (
            <div className="flex h-7 min-w-0 items-center gap-1.5">
              <span
                className="min-w-0 truncate text-appkit-small font-medium leading-7 text-[var(--agent-aurora-text-muted)]"
                title={t("followUp")}
              >
                {t("followUp")}
              </span>
              <Button
                type="button"
                size="icon"
                variant="secondary"
                onClick={(event) => {
                  event.stopPropagation();
                  onAskFollowUp("voice");
                }}
                className="h-7 w-7 shrink-0 p-0"
                aria-label={t("askFollowUpByVoice")}
                data-testid={`${testId}-ask-follow-up-voice`}
              >
                <CONCEPT_ICONS.voice className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="secondary"
                onClick={(event) => {
                  event.stopPropagation();
                  onAskFollowUp("text");
                }}
                className="h-7 w-7 shrink-0 p-0"
                aria-label={t("typeFollowUp")}
                data-testid={`${testId}-ask-follow-up-text`}
              >
                <CONCEPT_ICONS.chat className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : null}
          {cursor.targetFrameId ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={(event) => {
                event.stopPropagation();
                onNavigateToFrame();
              }}
              className={cn(
                "h-7 shrink-0 gap-1.5 px-2 text-appkit-small",
                compact ? "w-full" : "col-start-2",
              )}
              data-testid={`${testId}-show-deliverables`}
            >
              <Icons.MousePointer className="h-3 w-3" />
              {t("showDeliverables")}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export type { AgentAvatarCardContentProps };
